import { nextTick } from "vue";
import { cloneDeep, randomId } from "@/shared/utils/helpers.js";
import { copy } from "@/shared/utils/string";
import { EventBus } from "@/shared/projects/event-bus.ts";
import { iframeMutations } from "@/shared/store/iframe.mutations";
import { pricingUploadService } from "../services/pricing-upload.service";
import { pricingCartService } from "../services/pricing-cart.service";
import { preparePricing } from "@/shared/projects/prepareData.js";
import { pricingSnapshotService } from "@/apps/pricing/store/pricing-snapshot.service";
import { t } from "@/i18n";
import { projectsService } from "@/shared/projects/projects.service";

export default {
    namespaced: true,
    state: () => ({
        pricing: {},
        categories: [],
        socialsGhost: {},
        ecPricing: {},
        ecVariants: [],
        iframe: null,
        iframeLoaded: false,
        hasChanges: false,
        hasSnapshot: false,
    }),
    getters: {
        appUrl: (s, g, rootS, rootGetters) => rootGetters.easyUrls.pricing,
        nVariants: (state) => {
            let n = 0;
            if (state.categories && state.categories.length) {
                state.categories
                    .filter((c) => !c._deleted)
                    .forEach((category) => {
                        n += category.variants.length;
                    });
            }
            return n;
        },
        pricingUuid: (state) => {
            const { ecId, ecAll, uuid } = state.pricing;

            return ecId
                ? ecAll
                    ? `ec_tenant_${state.ecPricing.tenantSlug}`
                    : `ec_product_${state.ecPricing.productSlug}`
                : uuid;
        },
        pricingLink: (state, g, rootState) => {
            const { ecId, ecAll, uuid } = state.pricing;

            return ecId
                ? `${rootState.globalUrl}/ec/${state.ecPricing.tenantSlug}${
                      ecAll ? "" : `/${state.ecPricing.productSlug}`
                  }`
                : `${rootState.globalUrl}/pricings/${uuid}`;
        },
        pricingIframe: (state, getters) => {
            return `<iframe
                id="${getters.pricingUuid}"
                width="100%"
                src="${getters.pricingLink}"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
                frameborder="0"
                scrolling="no"
                ></iframe>
                <script> window.addEventListener("message", (e) => {!!e.data.frameHeight && e.data.pricingUuid === "${getters.pricingUuid}" && (document.getElementById("${getters.pricingUuid}").style.height = e.data.frameHeight);});</script>`;
        },
    },
    mutations: {
        ...iframeMutations,
        setProject(state, { pricing, categories, ecPricing, ecVariants, snapshot = {} }) {
            state.pricing = pricingSnapshotService.makeSnapshotState(
                state,
                "pricing",
                pricing,
                snapshot
            );
            state.categories = pricingSnapshotService.makeSnapshotState(
                state,
                "categories",
                categories,
                snapshot
            );
            state.pricing.socials = pricingSnapshotService.makeSnapshotState(
                state,
                "socialsGhost",
                state.pricing.socials || {},
                snapshot
            );
            // No need for snapshot for:
            state.ecPricing = cloneDeep(ecPricing);
            state.ecVariants = cloneDeep(ecVariants);
            state.hasChanges = false;
        },
        clearProject(state) {
            state.pricing = {};
            state.categories = [];
            state.ecTenantSlug = "";
            state.ecProductSlug = "";
            pricingSnapshotService.clearSnapshot(state, "pricing", {});
            pricingSnapshotService.clearSnapshot(state, "categories", []);
            pricingSnapshotService.clearSnapshot(state, "socialsGhost", {});
        },
        addVariant(state, variant) {
            const category = state.categories.find((x) => x.id === variant.categoryId);
            if (category) {
                variant["_created"] = true;
                variant.id = randomId();
                variant.order = category.variants.length;
                variant.hide = false;
                category.variants.push(variant);
            }
        },
        changeVariant(state, payload) {
            const updatedVariant = Object.assign({}, payload);

            const category = state.categories.find((x) => x.id === payload.categoryId);
            const index = category.variants.map((x) => x.id).indexOf(payload.id);
            if (index > -1) {
                category.variants.splice(index, 1, updatedVariant);
            }
        },
        setVariants(state) {
            state.categories.forEach((category) => {
                category.variants
                    .filter((variant) => !variant._deleted)
                    .forEach((variant, index) => {
                        variant.categoryId = category.id;
                        variant.order = index;
                        if (!variant._created && !variant._deleted) {
                            variant["_changed"] = true;
                        }
                    });
            });
        },
        removeVariant(state, variant) {
            if (variant._created) {
                const category = state.categories.find((x) => x.id === variant.categoryId);
                const index = category.variants.map((x) => x.id).indexOf(variant.id);
                if (index > -1) {
                    category.variants.splice(index, 1);
                }
            } else {
                if (variant._changed) {
                    delete variant._changed;
                }
                variant["_deleted"] = true;
            }
        },
        addCategory(state, name) {
            state.categories.push({
                _created: true,
                id: randomId(),
                pricingId: state.pricing.id,
                name: name,
                variants: [],
            });
        },
        removeCategory(state, category) {
            if (category._created) {
                const index = state.categories.indexOf(category);
                if (index > -1) {
                    state.categories.splice(index, 1);
                }
            } else {
                delete category._changed;
                category["_deleted"] = true;
                category.variants.forEach((variant, index) => {
                    if (variant._created) {
                        category.variants.splice(index, 1);
                    } else {
                        delete variant._changed;
                        variant["_deleted"] = true;
                    }
                });
            }
        },
    },
    actions: {
        refreshIframe({ state, getters }) {
            const pricing = cloneDeep(state.pricing);
            pricing.categories = cloneDeep(state.categories);
            pricing.ecPricing = cloneDeep(state.ecPricing);
            pricing.ecVariants = cloneDeep(state.ecVariants);
            state.iframe = document.querySelector("#iframe");
            if (state.iframe && pricing.id) {
                state.iframe.contentWindow.postMessage({ pricingData: pricing }, getters.appUrl);
            }
        },
        removeVariant({ commit }, payload) {
            commit("removeVariant", payload);
            commit("setVariants");
        },
        async uploadVariantImg({ rootState }, payload) {
            rootState.waiting = true;

            try {
                const { img } = await pricingUploadService.variantImage({
                    file: payload.file,
                    variantId: payload.variant.id,
                });

                payload.variant.img = img;
                rootState.waiting = false;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        async uploadPricingImg({ state, rootState }, payload) {
            rootState.waiting = true;

            try {
                const { img } = await pricingUploadService.pricingImage({
                    file: payload.file,
                    uuid: state.pricing.uuid,
                });

                state.pricing.img = img;
                rootState.waiting = false;
                return img;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        async uploadPricingBackImg({ state, rootState }, payload) {
            rootState.waiting = true;

            try {
                const { img } = await pricingUploadService.pricingBackImage({
                    file: payload.file,
                    uuid: state.pricing.uuid,
                });

                state.pricing.backImg = img;
                rootState.waiting = false;
                return img;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        cloneVariant({ commit }, variant) {
            const clonedVariant = cloneDeep(variant);
            commit("addVariant", clonedVariant);
        },
        showNoVariantsModal({ commit }) {
            commit(
                "showModal",
                {
                    type: "info",
                    title: t("pricing.empty.title"),
                    desc: t("pricing.empty.desc"),
                    cancelBtn: t("shared.cancel"),
                    actionBtn: t("pricing.empty.cta"),
                    callback: () => {
                        EventBus.$emit("openSidebar", "data");
                        setTimeout(() => EventBus.$emit("createVariant"), 100);
                    },
                },
                { root: true }
            );
        },
        publishProject({ getters, rootGetters, dispatch }) {
            if (!rootGetters.logged) {
                dispatch("showUnloggedClickModal", null, { root: true });
                return;
            }
            if (!getters.nVariants) {
                dispatch("showNoVariantsModal");
                return;
            }
            dispatch("hostCode");
        },
        async unpublishPricing({ state, commit, dispatch }) {
            await dispatch("unpublish", { uuid: state.pricing.uuid });
            commit("setUnpublished", state.pricing, { root: true });
            dispatch("pricing/projects/mergeSnapshotChanges", null, { root: true });
        },
        // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
        async unpublish({ _ }, { uuid }) {
            return projectsService.publish({ tool: "pricing", uuid, publish: false });
        },
        showLinkModal({ getters, commit }) {
            const modalData = {
                type: "code",
                title: t("pricing.share_modal.title"),
                code: getters.pricingIframe,
                actionBtn: t("shared.copy_embed"),
                cancelBtn: t("pricing.share_modal.copy_link"),
                callback: () => {
                    copy(getters.pricingIframe);
                    commit(
                        "showAlert",
                        {
                            type: "success",
                            title: t("shared.code_copied"),
                        },
                        { root: true }
                    );
                },
                cancelCallback: () => {
                    copy(getters.pricingLink);
                    commit(
                        "showAlert",
                        {
                            type: "success",
                            title: t("shared.link_copied"),
                        },
                        { root: true }
                    );
                },
            };
            commit("showModal", modalData, { root: true });
        },
        async hostCode({ state, rootState, rootGetters, commit, dispatch }) {
            rootState.waiting = true;
            const firstPublish = !rootGetters.isPublished(state.pricing);

            await nextTick();

            try {
                await projectsService.publish({
                    tool: "pricing",
                    uuid: state.pricing.uuid,
                    publish: true,
                });

                commit("setPublished", state.pricing, { root: true });
                dispatch("pricing/projects/mergeSnapshotChanges", null, { root: true });
                if (firstPublish) {
                    dispatch("showLinkModal");
                }
                rootState.waiting = false;
            } catch {
                rootState.waiting = false;
            }
        },
        changeFontFamily({ state }, payload) {
            state.pricing.fontFamily = payload;
        },
        async getECProducts({ rootState, rootGetters, dispatch }) {
            if (!rootGetters.logged) {
                dispatch("showUnloggedClickModal", null, { root: true });
                return;
            }

            rootState.waiting = true;
            const data = await pricingCartService.getECProducts();
            rootState.waiting = false;
            return data;
        },
        async addECProducts({ state, rootState, commit }, payload) {
            rootState.waiting = true;

            try {
                const data = await pricingCartService.addECProducts({
                    uuid: state.pricing.uuid,
                    mode: payload.mode,
                    isSingleProduct: payload.isSingleProduct,
                    ecId: payload.ecId,
                });

                const { pricing, ecPricing, ecVariants } = data;
                preparePricing(pricing);

                commit("setProject", {
                    categories: pricing.categories,
                    pricing,
                    ecPricing,
                    ecVariants,
                });

                commit(
                    "showAlert",
                    {
                        type: "success",
                        title: t("pricing.product.success"),
                    },
                    { root: true }
                );
                rootState.waiting = false;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        // needed to make that to retrieve proper category Id!
        addCategory({ state, commit }, name) {
            commit("addCategory", name);
            return state.categories[state.categories.length - 1].id;
        },
        async fillWithExample({ state, commit }, template) {
            const categories = state.categories;

            if (template === "empty") {
                commit("addVariant", {
                    title: t("pricing.templates.empty_title"),
                    desc: t("pricing.templates.empty_desc"),
                    price: t("pricing.templates.price", { price: 10 }),
                    promoPrice: t("pricing.templates.price", { price: 15 }),
                    categoryId: categories[0].id,
                });
            }

            if (template === "subscription") {
                state.pricing.title = t("pricing.templates.sub_titile");
                state.pricing.subtitle = t("pricing.templates.sub_subtitle");
                categories[0]._changed = true;
                categories[0].name = t("pricing.templates.monthly");
                categories[0].variants = [
                    {
                        _created: true,
                        price: t("pricing.templates.price", { price: 10 }),
                        title: t("pricing.templates.basic_plan"),
                        desc: t("pricing.templates.sub_basic_desc"),
                        points: `+ ${t("pricing.templates.basic_feature")}\nx ${t("pricing.templates.pro_feature")}\nx ${t("pricing.templates.premium_feature")}`,
                        categoryId: categories[0].id,
                    },
                    {
                        _created: true,
                        title: t("pricing.templates.pro_plan"),
                        price: t("pricing.templates.price", { price: 20 }),
                        promoPrice: t("pricing.templates.price", { price: 25 }),
                        desc: "Upgrade to our Pro plan for advanced features and greater flexibility, perfect for growing businesses.",
                        points: `+ ${t("pricing.templates.basic_feature")}\n+ ${t("pricing.templates.pro_feature")}\nx ${t("pricing.templates.premium_feature")}`,
                        categoryId: categories[0].id,
                        highlight: true,
                        badge: "Bestseller",
                    },
                    {
                        _created: true,
                        title: t("pricing.templates.premium_plan"),
                        price: t("pricing.templates.price", { price: 30 }),
                        promoPrice: t("pricing.templates.price", { price: 35 }),
                        desc: "Experience the full power with our Premium plan, designed for large teams and enterprises needing comprehensive solutions.",
                        points: `+ ${t("pricing.templates.basic_feature")}\n+ ${t("pricing.templates.pro_feature")}\n+ ${t("pricing.templates.premium_feature")}`,
                        categoryId: categories[0].id,
                    },
                ];
                const id = randomId();
                categories[1] = {
                    _created: true,
                    name: t("pricing.templates.yearly"),
                    id,
                    pricingId: state.pricing.id,
                    variants: [
                        {
                            _created: true,
                            title: t("pricing.templates.basic_plan"),
                            price: t("pricing.templates.price", { price: 120 }),
                            promoPrice: t("pricing.templates.price", { price: 100 }),
                            desc: t("pricing.templates.sub_basic_desc"),
                            points: `+ ${t("pricing.templates.basic_feature")}\nx ${t("pricing.templates.pro_feature")}\nx ${t("pricing.templates.premium_feature")}`,
                            categoryId: id,
                        },
                        {
                            _created: true,
                            title: t("pricing.templates.pro_plan"),
                            price: t("pricing.templates.price", { price: 240 }),
                            promoPrice: t("pricing.templates.price", { price: 200 }),
                            desc: t("pricing.templates.sub_pro_desc"),
                            points: `+ ${t("pricing.templates.basic_feature")}\n+ ${t("pricing.templates.pro_feature")}\nx ${t("pricing.templates.premium_feature")}`,
                            categoryId: id,
                            highlight: true,
                            badge: "Bestseller",
                        },
                        {
                            _created: true,
                            title: t("pricing.templates.premium_plan"),
                            price: t("pricing.templates.price", { price: 360 }),
                            promoPrice: t("pricing.templates.price", { price: 300 }),
                            desc: t("pricing.templates.sub_premium_desc"),
                            points: `+ ${t("pricing.templates.basic_feature")}\n+ ${t("pricing.templates.pro_feature")}\n+ ${t("pricing.templates.premium_feature")}`,
                            categoryId: id,
                        },
                    ],
                };
            }

            if (template === "ebook") {
                state.pricing.title = t("pricing.templates.ebook_title");
                state.pricing.subtitle = t("pricing.templates.ebook_subtitle");
                categories[0]._changed = true;
                categories[0].name = "E-book";
                categories[0].variants = [
                    {
                        _created: true,
                        price: t("pricing.templates.price", { price: 5 }),
                        title: "E-book",
                        desc: t("pricing.templates.ebook_basic_desc"),
                        points: `+ ${t("pricing.templates.basic_content")}\nx ${t("pricing.templates.extra_examples")}\nx ${t("pricing.templates.individual_training")}`,
                        categoryId: categories[0].id,
                    },
                    {
                        _created: true,
                        title: `E-book + ${t("pricing.templates.examples")}`,
                        price: t("pricing.templates.price", { price: 15 }),
                        promoPrice: t("pricing.templates.price", { price: 10 }),
                        desc: t("pricing.templates.ebook_pro_desc"),
                        points: `+ ${t("pricing.templates.basic_content")}\n+ ${t("pricing.templates.extra_examples")}\nx ${t("pricing.templates.individual_training")}`,
                        categoryId: categories[0].id,
                        highlight: true,
                        badge: "Bestseller",
                    },
                    {
                        _created: true,
                        title: `E-book + ${t("pricing.templates.examples")} + ${t("pricing.templates.training")}`,
                        price: t("pricing.templates.price", { price: 25 }),
                        promoPrice: t("pricing.templates.price", { price: 20 }),
                        desc: t("pricing.templates.ebook_premium_desc"),
                        points: `+ ${t("pricing.templates.basic_content")}\n+ ${t("pricing.templates.extra_examples")}\n+ ${t("pricing.templates.individual_training")}`,
                        categoryId: categories[0].id,
                    },
                ];
            }

            commit(
                "showAlert",
                {
                    type: "success",
                    title: t("pricing.templates.import_success"),
                },
                { root: true }
            );
        },
    },
};
